import { readStoredToken } from "./auth";
import type { OrderConsentPayload } from "../order-consent";

export type ProjectStatus =
  | "questionnaire_received"
  | "draft_in_progress"
  | "draft_ready"
  | "draft_sent"
  | "revision_requested"
  | "ordered"
  | "assets_pending"
  | "finalizing"
  | "live";

export interface Pricing {
  setup: number;
  monthly: number;
}

export interface CurrentDraftSummary {
  id: string;
  title: string;
  version: number;
  previewUrl: string;
  shareSlug: string;
  previewPassword: string;
  notes: string;
  personalMessage: string;
  sentAt: string | null;
  approvedAt: string | null;
}

export interface ProjectSummary {
  id: string;
  title: string;
  status: ProjectStatus;
  internalNotes: string;
  liveUrl: string;
  finalNotes: string;
  pricing: Pricing;
  revisions: {
    included: number;
    used: number;
    remaining: number;
    submitted: number;
  };
  createdAt: string;
  updatedAt: string;
  customer: {
    userId: string;
    name: string;
    email: string;
    company: string;
  };
  questionnaire: {
    id: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    contactCompany: string;
    createdAt: string;
  };
  currentDraft: CurrentDraftSummary | null;
  order: OrderSummary | null;
  paymentIssue: PaymentIssueSummary | null;
  assetCount: number;
  lastEmailSentAt: string | null;
}

export interface Draft {
  id: string;
  projectId: string;
  version: number;
  title: string;
  previewUrl: string;
  shareSlug: string;
  previewPassword: string;
  notes: string;
  personalMessage: string;
  sentAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RevisionRequest {
  id: string;
  projectId: string;
  draftId: string | null;
  roundNumber: number;
  feedback: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectAsset {
  id: string;
  projectId: string;
  category: "file" | "server_access" | "note";
  label: string;
  description: string;
  originalFileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  downloadUrl: string;
  contentText: string;
  createdAt: string;
}

export interface EmailLog {
  id: string;
  templateKey: string;
  recipientEmail: string;
  subject: string;
  bodyText: string;
  deliveryMode: string;
  deliveryStatus: string;
  sentAt: string | null;
  createdAt: string;
}

export interface OrderSummary {
  id: string;
  status: string;
  acceptedAt: string;
  acceptedByName: string;
  acceptedByEmail: string;
  paymentStatus: string | null;
  isPaymentConfirmed: boolean;
}

export interface PaymentIssueSummary {
  status: string;
  occurredAt: string | null;
  paymentStatus: string | null;
  checkoutSessionId: string | null;
  message: string;
}

export interface CustomerAccount {
  id: string;
  email: string;
  name: string;
  company: string;
  role: string;
  projectCount: number;
  createdAt: string;
}

export interface ProjectDetail extends ProjectSummary {
  questionnaire: ProjectSummary["questionnaire"] & {
    status: string;
    submissionPayload: Record<string, unknown>;
    updatedAt: string | null;
  };
  drafts: Draft[];
  revisionsList: RevisionRequest[];
  assets: ProjectAsset[];
  emails: EmailLog[];
}

export interface QuestionnaireSubmissionResult {
  project: ProjectDetail;
  adminEmail: EmailLog;
  customerEmail: EmailLog;
}

export interface DraftUploadFilePayload {
  fileName: string;
  relativePath: string;
  mimeType: string;
  sizeBytes: number;
  base64Data: string;
}

export interface PublicDraftInfo {
  id: string;
  projectId: string;
  title: string;
  notes: string;
  personalMessage: string;
  projectTitle: string;
  sentAt: string;
  pricing: Pricing;
  previewUrl?: string;
}

export interface ProjectCheckoutSession {
  url: string | null;
  checkoutSessionId: string;
  expiresAt: string | null;
  uiMode: "hosted";
}

export interface ProjectCheckoutSessionStatus {
  id: string;
  status: string;
  paymentStatus: string | null;
  customerEmail: string | null;
}

export interface PublicDraftOrderSuccess {
  projectId: string;
  projectTitle: string;
  customerName: string;
  customerEmail: string;
  mustSetPassword: boolean;
  orderAcceptedAt: string | null;
  checkoutStatus: string | null;
  paymentStatus: string | null;
  isPaymentConfirmed: boolean;
  hasPaymentIssue: boolean;
  paymentIssueMessage: string | null;
}

export interface BillingInvoiceSummary {
  id: string;
  number: string | null;
  status: string | null;
  currency: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  createdAt: string | null;
  paidAt: string | null;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
  description: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface ProjectBillingOverview {
  available: boolean;
  reason: string | null;
  paymentStatus: string | null;
  isPaymentConfirmed: boolean;
  subscription: {
    id: string;
    status: string | null;
    currency: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAt: string | null;
  } | null;
  upcomingInvoice: {
    amountDue: number;
    total: number;
    currency: string;
    periodStart: string | null;
    periodEnd: string | null;
    nextPaymentAttempt: string | null;
  } | null;
  invoices: BillingInvoiceSummary[];
}

export class WorkflowApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "WorkflowApiError";
    this.status = status;
  }
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/+$/, "");

function joinUrl(baseUrl: string, path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

function getApiEndpoint(path: string) {
  return joinUrl(API_BASE_URL, path);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getErrorMessage(payload: unknown, fallback: string) {
  if (!isRecord(payload)) {
    return fallback;
  }

  const message = payload.message || payload.error;
  return typeof message === "string" && message.trim() ? message : fallback;
}

function getStatusFallbackMessage(status?: number) {
  if (status === 400) {
    return "Die Anfrage konnte nicht verarbeitet werden. Bitte prüfen Sie Ihre Eingaben.";
  }

  if (status === 401) {
    return "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.";
  }

  if (status === 403) {
    return "Diese Aktion ist aktuell nicht erlaubt.";
  }

  if (status === 404) {
    return "Die angeforderte Seite oder Aktion wurde nicht gefunden.";
  }

  if (status === 413) {
    return "Die übermittelten Daten sind zu groß. Bitte laden Sie kleinere oder weniger Dateien gleichzeitig hoch.";
  }

  if (status === 429) {
    return "Es wurden zu viele Anfragen in kurzer Zeit gesendet. Bitte versuchen Sie es gleich erneut.";
  }

  if (status && status >= 500) {
    return "Auf unserer Seite ist gerade ein technisches Problem aufgetreten. Bitte versuchen Sie es in wenigen Minuten erneut.";
  }

  return "Anfrage fehlgeschlagen";
}

function shouldReplaceErrorMessage(message: string, status?: number) {
  const normalizedMessage = message.trim().toLowerCase();

  if (!normalizedMessage) {
    return true;
  }

  if (
    normalizedMessage === "interner serverfehler" ||
    normalizedMessage === "internal server error" ||
    normalizedMessage === "anfrage fehlgeschlagen"
  ) {
    return true;
  }

  if (status && status >= 500 && normalizedMessage.includes("server error")) {
    return true;
  }

  return false;
}

function getFriendlyApiErrorMessage(status: number | undefined, payload: unknown, fallback?: string) {
  const message = getErrorMessage(payload, "");

  if (message && !shouldReplaceErrorMessage(message, status)) {
    return message;
  }

  return fallback || getStatusFallbackMessage(status);
}

async function requestJson<T>(path: string, init: RequestInit = {}, authRequired = false): Promise<T> {
  const token = readStoredToken();
  const response = await fetch(getApiEndpoint(path), {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(authRequired && token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });

  const rawText = await response.text();
  let payload: unknown = null;

  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      if (!response.ok) {
        throw new WorkflowApiError(getStatusFallbackMessage(response.status), response.status);
      }

      throw new WorkflowApiError("Der Server hat keine gültige Antwort geliefert", response.status);
    }
  }

  if (!response.ok) {
    throw new WorkflowApiError(getFriendlyApiErrorMessage(response.status, payload), response.status);
  }

  return payload as T;
}

export async function submitQuestionnaire(formData: Record<string, unknown>) {
  return requestJson<QuestionnaireSubmissionResult>(
    "/questionnaires",
    {
      method: "POST",
      body: JSON.stringify(formData),
    },
    false,
  );
}

export async function fetchAdminProjects() {
  const payload = await requestJson<{ projects: ProjectSummary[] }>(
    "/admin/projects",
    { method: "GET" },
    true,
  );
  return payload.projects;
}

export async function fetchAdminCustomerAccounts() {
  const payload = await requestJson<{ accounts: CustomerAccount[] }>(
    "/admin/customer-accounts",
    { method: "GET" },
    true,
  );
  return payload.accounts;
}

export async function deleteAdminCustomerAccount(userId: string) {
  const payload = await requestJson<{
    deletedAccount: {
      id: string;
      email: string;
      name: string;
      company: string;
    };
  }>(
    `/admin/customer-accounts/${userId}`,
    { method: "DELETE" },
    true,
  );
  return payload.deletedAccount;
}

export async function fetchAdminProject(projectId: string) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/admin/projects/${projectId}`,
    { method: "GET" },
    true,
  );
  return payload.project;
}

export async function updateAdminProject(
  projectId: string,
  updates: Partial<{
    status: ProjectStatus;
    internalNotes: string;
    liveUrl: string;
    finalNotes: string;
  }>,
) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/admin/projects/${projectId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
    true,
  );
  return payload.project;
}

export async function createAdminDraft(
  projectId: string,
  draft: {
    files: DraftUploadFilePayload[];
  },
) {
  const payload = await requestJson<{ project: ProjectDetail; draftId: string }>(
    `/admin/projects/${projectId}/drafts`,
    {
      method: "POST",
      body: JSON.stringify(draft),
    },
    true,
  );
  return payload;
}

export async function updateAdminDraft(
  draftId: string,
  updates: Partial<{
    title: string;
    previewUrl: string;
    previewPassword: string;
    notes: string;
  }>,
) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/admin/drafts/${draftId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
    true,
  );
  return payload.project;
}

export async function sendAdminDraft(
  draftId: string,
  body: Partial<{
    personalMessage: string;
  }> = {},
) {
  return requestJson<{
    project: ProjectDetail;
    email: EmailLog;
  }>(
    `/admin/drafts/${draftId}/send`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    true,
  );
}

export async function fetchCustomerProjects() {
  const payload = await requestJson<{ projects: ProjectSummary[] }>(
    "/customer/projects",
    { method: "GET" },
    true,
  );
  return payload.projects;
}

export async function fetchCustomerProject(projectId: string) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/customer/projects/${projectId}`,
    { method: "GET" },
    true,
  );
  return payload.project;
}

export async function submitProjectOrder(
  projectId: string,
  body: Partial<{
    acceptedByName: string;
    acceptedByEmail: string;
    billingNotes: string;
  }> &
    Pick<OrderConsentPayload, "customerType">,
) {
  return requestJson<ProjectCheckoutSession>(
    `/customer/projects/${projectId}/order`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    true,
  );
}

export async function confirmProjectOrder(projectId: string, sessionId: string) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/customer/projects/${projectId}/order/confirm`,
    {
      method: "POST",
      body: JSON.stringify({ sessionId }),
    },
    true,
  );
  return payload.project;
}

export async function fetchProjectOrderSessionStatus(projectId: string, sessionId: string) {
  const payload = await requestJson<{ session: ProjectCheckoutSessionStatus }>(
    `/customer/projects/${projectId}/order/session-status?sessionId=${encodeURIComponent(sessionId)}`,
    {
      method: "GET",
    },
    true,
  );
  return payload.session;
}

export async function fetchProjectBilling(projectId: string) {
  const payload = await requestJson<{ billing: ProjectBillingOverview }>(
    `/customer/projects/${projectId}/billing`,
    {
      method: "GET",
    },
    true,
  );
  return payload.billing;
}

export async function updateProjectBillingSubscription(
  projectId: string,
  body: {
    cancelAtPeriodEnd: boolean;
  },
) {
  const payload = await requestJson<{ billing: ProjectBillingOverview }>(
    `/customer/projects/${projectId}/billing/subscription`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
    true,
  );
  return payload.billing;
}

export async function submitProjectRevision(projectId: string, feedback: string) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/customer/projects/${projectId}/revisions`,
    {
      method: "POST",
      body: JSON.stringify({ feedback }),
    },
    true,
  );
  return payload.project;
}

export async function submitProjectAssets(
  projectId: string,
  body: {
    serverAccessNotes?: string;
    generalNotes?: string;
    assets: Array<{
      fileName: string;
      mimeType: string;
      sizeBytes: number;
      base64Data: string;
    }>;
  },
) {
  const payload = await requestJson<{ project: ProjectDetail }>(
    `/customer/projects/${projectId}/assets`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    true,
  );
  return payload.project;
}

export async function fetchPublicDraft(shareSlug: string) {
  const payload = await requestJson<{ draft: PublicDraftInfo }>(
    `/public/drafts/${shareSlug}`,
    { method: "GET" },
    false,
  );
  return payload.draft;
}

export async function unlockPublicDraft(shareSlug: string, password: string) {
  const payload = await requestJson<{ draft: PublicDraftInfo }>(
    `/public/drafts/${shareSlug}/unlock`,
    {
      method: "POST",
      body: JSON.stringify({ password }),
    },
    false,
  );
  return payload.draft;
}

export async function createPublicDraftOrder(
  shareSlug: string,
  body: Pick<OrderConsentPayload, "customerType">,
) {
  return requestJson<ProjectCheckoutSession>(
    `/public/drafts/${shareSlug}/order`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    false,
  );
}

export async function fetchPublicDraftOrderSessionStatus(shareSlug: string, sessionId: string) {
  const payload = await requestJson<{ session: ProjectCheckoutSessionStatus }>(
    `/public/drafts/${shareSlug}/order/session-status?sessionId=${encodeURIComponent(sessionId)}`,
    {
      method: "GET",
    },
    false,
  );
  return payload.session;
}

export async function confirmPublicDraftOrder(shareSlug: string, sessionId: string) {
  const payload = await requestJson<{ draft: PublicDraftInfo }>(
    `/public/drafts/${shareSlug}/order/confirm`,
    {
      method: "POST",
      body: JSON.stringify({ sessionId }),
    },
    false,
  );
  return payload.draft;
}

export async function fetchPublicDraftOrderSuccess(shareSlug: string, sessionId: string) {
  const payload = await requestJson<{ success: PublicDraftOrderSuccess }>(
    `/public/drafts/${shareSlug}/order/success?sessionId=${encodeURIComponent(sessionId)}`,
    {
      method: "GET",
    },
    false,
  );
  return payload.success;
}

export async function setupPublicDraftOrderAccount(
  shareSlug: string,
  body: {
    sessionId: string;
    password: string;
  },
) {
  const payload = await requestJson<{ success: PublicDraftOrderSuccess }>(
    `/public/drafts/${shareSlug}/order/account-setup`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    false,
  );
  return payload.success;
}
